// API cổng khách tự mua (plan D, spec 22/08/2026 §8) — khách tự trả nốt công
// nợ, tự đặt mua ảnh in, tự mở lại màn chọn ảnh.
//
// Cùng cookie phiên SĐT với portal đơn hàng: `credentials: "include"`, KHÔNG
// đi qua client bearer của nhân viên. Thiếu cookie ở một lời gọi là khách
// đang trả tiền thì bị hỏi đăng nhập lại.
//
// FE KHÔNG bao giờ gửi kèm giá: máy chủ tự định giá cả phiếu thu lẫn đơn in.
import { serverErrorCode, serverErrorMessage } from "@/lib/api/error-message";
import type { components } from "@/lib/api/generated/schema";

const apiBaseUrl = import.meta.env.VITE_API_URL ?? "http://localhost:8080/api/v1";

export type PortalQR = components["schemas"]["PublicPortalQR"];
export type PortalPaymentStatus = components["schemas"]["PublicPortalPaymentStatus"];
export type PortalShop = components["schemas"]["PublicPortalShop"];
export type PortalPrintOrderInput = components["schemas"]["PublicPortalPrintOrderInput"];
export type PortalPrintOrderResponse = components["schemas"]["PublicPortalPrintOrderResponse"];
export type PortalSelectionReopen = components["schemas"]["PublicPhotoSelectionReopen"];

/** Lỗi API cổng khách — giữ mã lỗi của máy chủ để màn hình phân nhánh (409 → hết hạn/đã trả). */
export class PortalApiError extends Error {
  code: string;
  status: number;

  constructor(message: string, code: string, status: number) {
    super(message);
    this.name = "PortalApiError";
    this.code = code;
    this.status = status;
  }
}

async function errorFrom(response: Response, fallback: string): Promise<PortalApiError> {
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    // Proxy chèn trang HTML thì giữ câu dự phòng — không để lỗi đọc JSON
    // hất khách ra màn trắng.
  }
  return new PortalApiError(serverErrorMessage(body, fallback), serverErrorCode(body) || "unknown", response.status);
}

async function portalFetch<T>(
  path: string,
  fallback: string,
  init?: RequestInit & { signal?: AbortSignal | null },
): Promise<T> {
  const response = await fetch(`${apiBaseUrl}/public/customer-orders/${path}`, {
    credentials: "include",
    ...init,
    signal: init?.signal ?? null,
  });
  if (!response.ok) throw await errorFrom(response, fallback);
  return (await response.json()) as T;
}

const jsonInit = (body: unknown): RequestInit => ({
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify(body),
});

/**
 * Mở phiếu thu chờ xác nhận + nội dung chuyển khoản.
 * Bỏ trống `amount` = trả hết số còn phải thu (gửi 0 theo đúng hợp đồng).
 */
export function createPortalQR(orderId: string, amount?: number): Promise<PortalQR> {
  return portalFetch<PortalQR>(
    `${orderId}/payments/qr`,
    "Không tạo được mã QR",
    jsonInit({ amount: amount ?? 0 }),
  );
}

/** Màn chờ hỏi lại mỗi 3 giây — luôn truyền signal để rời màn là dừng hỏi. */
export function fetchPortalPayment(
  orderId: string,
  paymentId: string,
  init?: { signal?: AbortSignal },
): Promise<PortalPaymentStatus> {
  return portalFetch<PortalPaymentStatus>(
    `${orderId}/payments/${paymentId}`,
    "Không đọc được trạng thái thanh toán",
    { signal: init?.signal ?? null },
  );
}

export function fetchPortalShop(
  orderId: string,
  init?: { signal?: AbortSignal },
): Promise<PortalShop> {
  return portalFetch<PortalShop>(`${orderId}/shop`, "Không tải được danh sách sản phẩm", {
    signal: init?.signal ?? null,
  });
}

export function createPortalPrintOrder(
  orderId: string,
  input: PortalPrintOrderInput,
): Promise<PortalPrintOrderResponse> {
  return portalFetch<PortalPrintOrderResponse>(
    `${orderId}/print-orders`,
    "Không đặt được sản phẩm in",
    jsonInit(input),
  );
}

/** Mở lại màn chọn ảnh album — trả đường dẫn /chon-anh còn hạn. */
export function reopenPortalSelection(orderId: string): Promise<PortalSelectionReopen> {
  return portalFetch<PortalSelectionReopen>(
    `${orderId}/photo-selection/reopen`,
    "Không mở lại được chọn ảnh",
    { method: "POST" },
  );
}
